// Google Maps API loading service
export class GoogleMapsService {
  private static isLoading = false;
  private static isLoaded = false;
  private static callbacks: (() => void)[] = [];
  private static errorCallbacks: ((error: Error) => void)[] = [];
  private static apiKey: string | null = null;
  private static readonly GOOGLE_MAPS_CALLBACK_NAME = 'onGoogleMapsApiReady';

  /**
   * Load the Google Maps API with the given API key
   * @param apiKey - The Google Maps API key
   * @returns Promise that resolves when the API is loaded
   */
  static load(apiKey: string): Promise<void> {
    this.apiKey = apiKey;

    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (this.isLoaded && window.google && window.google.maps) {
        resolve();
        return;
      }

      // Add callbacks to queues
      this.callbacks.push(resolve);
      this.errorCallbacks.push(reject);

      // If already loading, just wait for the callbacks
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      // Setup global callbacks for Google Maps script
      (window as any)[this.GOOGLE_MAPS_CALLBACK_NAME] = this.handleScriptLoad.bind(this);
      (window as any).gm_authFailure = () => {
        this.handleScriptError(new Error('Google Maps authentication failed. Check your API key.'));
      };

      // Create and load the script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=${this.GOOGLE_MAPS_CALLBACK_NAME}`;
      script.async = true;
      script.onerror = () => {
        this.handleScriptError(new Error('Failed to load Google Maps script. Check network connection.'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Check if Google Maps API is loaded and available
   */
  static isApiLoaded(): boolean {
    return this.isLoaded && !!window.google && !!window.google.maps;
  }

  /**
   * Get the current API key
   */
  static getApiKey(): string | null {
    return this.apiKey;
  }

  private static cleanupGlobalHandlers(): void {
    delete (window as any)[this.GOOGLE_MAPS_CALLBACK_NAME];
    delete (window as any).gm_authFailure;
  }

  /**
   * Handle successful script loading
   */
  private static handleScriptLoad(): void {
    this.isLoaded = true;
    this.isLoading = false;
    
    // Execute all success callbacks
    this.callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in Google Maps load callback:', error);
      }
    });
    
    // Clear callbacks
    this.callbacks = [];
    this.errorCallbacks = [];
    this.cleanupGlobalHandlers();
  }

  /**
   * Handle script loading errors
   */
  private static handleScriptError(error: Error): void {
    this.isLoading = false;
    
    // Execute all error callbacks
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (err) {
        console.error('Error in Google Maps error callback:', err);
      }
    });
    
    // Clear callbacks
    this.callbacks = [];
    this.errorCallbacks = [];
    this.cleanupGlobalHandlers();
  }

  /**
   * Clean up and reset the service (useful for testing)
   */
  static reset(): void {
    this.isLoading = false;
    this.isLoaded = false;
    this.callbacks = [];
    this.errorCallbacks = [];
    this.apiKey = null;
    this.cleanupGlobalHandlers();
    
    // Remove existing script tags
    const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    existingScripts.forEach(script => script.remove());
  }
}

// Global type declarations
declare global {
  interface Window {
    google: any;
  }
}

export default GoogleMapsService; 